import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import LayoutWithMap from 'containers/Base/LayoutWithMap';
import { getUserAssignedDepartmentList } from 'reducers/users';
import useFilters from 'containers/Base/useFilters.js';
import {
  messagesNameSpace,
  messagePageActions,
  messageActions,
  messageSelectors,
  messageFilterActions,
} from 'containers/Pages/Messages';
import { filters } from 'containers/Pages/Messages/filters';
import { getGlobalUsersRecord } from 'reducers/globalList';
import { modalState, toggleModal, detailOpenState } from 'reducers/ui';
import { PAGE_URLS } from 'utils/constants';

const Messages = ({
  columns,
  filtersRequired,
  FormComponent,
  editable,
  details: MessageDetails,
  loading,
}) => {
  const [replyMsg, setReplyMsg] = useState(false);
  const [fwdMsg, setFwdMsg] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const detailOpen = useSelector(state => detailOpenState(state));
  const messageListData = useSelector(state =>
    messageSelectors.getMessageListTransformed(state),
  );
  const totalEntries = useSelector(state =>
    messageSelectors.getTotalEntries(state),
  );
  const filterValues = useSelector(state => messageSelectors.getFilters(state));
  const forceRefresh = useSelector(state =>
    messageSelectors.getForceRefresh(state),
  );
  const userAssignedDepartments = useSelector(state =>
    getUserAssignedDepartmentList(state),
  );
  const modalOpen = useSelector(state => modalState(state));
  const users = useSelector(state => getGlobalUsersRecord(state));
  const messageDetails = useSelector(state =>
    messageSelectors.getMessageDetailsTransformed(state),
  );
  //--------------------------customHooks--------------------------
  const { localFilterValues, updateFilter } = useFilters({
    filterValues,
  });
  //---------------------------Actions------------------------------
  const dispatch = useDispatch();
  const dispatchToggleModal = useCallback(
    () => dispatch(toggleModal(messagesNameSpace)),
    [dispatch],
  );
  const dispatchFetchMessagesList = useCallback(
    ({ filters, from }) =>
      dispatch(messageActions.fetchGridList({ filters, from })),
    [dispatch],
  );
  const fetchMessages = from => {
    dispatchFetchMessagesList({ filters: localFilterValues, from });
  };
  const onUpdate = () => {
    dispatchFetchMessagesList({ filters: localFilterValues });
  };
  //filter user list
  const constructPersonnelList = users => {
    const userList = [];
    Object.entries(users).forEach(([key, value]) => {
      userList.push({
        id: key,
        name: value,
      });
    });
    return userList;
  };
  useEffect(() => {
    if (Object.keys(messageDetails).length) {
      dispatch(messagePageActions.refreshMessageDetails());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageListData]);

  const MessageFormComponent = useCallback(
    props => (
      <FormComponent
        {...props}
        isReply={replyMsg}
        isFwd={fwdMsg}
        editMode={editMode}
        setReplyMsg={setReplyMsg}
        setFwdMsg={setFwdMsg}
        setEditMode={setEditMode}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [replyMsg, fwdMsg, editMode],
  );
  const MessageDetailComponent = useCallback(
    props => (
      <MessageDetails
        {...props}
        setReplyMsg={setReplyMsg}
        setFwdMsg={setFwdMsg}
        setEditMode={setEditMode}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <LayoutWithMap
      header="Messages"
      cols={columns}
      data={messageListData}
      totalEntries={totalEntries}
      loading={loading}
      forceRefresh={forceRefresh}
      //details
      DetailComponent={MessageDetailComponent}
      //creating and editing
      editable={editable}
      FormComponent={MessageFormComponent}
      toggleModal={dispatchToggleModal}
      onEdit={modalOpen[messagesNameSpace]}
      //filters
      filtersRequired={filtersRequired}
      filterValues={localFilterValues}
      filters={filters({
        filterValues,
        minDate: localFilterValues.dateFrom,
        users: constructPersonnelList(users),
        userAssignedDepartments,
      })}
      setFilters={(values, hasApplyFilter) =>
        updateFilter({
          values,
          hasApplyFilter,
          applyFilter: filters => {
            messageFilterActions.setFilterKey({ key: true });
            messageFilterActions.setFilters({ filters });
          },
        })
      }
      //end-filters
      onUpdate={onUpdate}
      fetchData={fetchMessages}
      persistentStateName={PAGE_URLS.MESSAGES.name}
      forceOpenDetailPanel={detailOpen[PAGE_URLS.MESSAGES.link]}
    />
  );
};
Messages.propTypes = {
  columns: PropTypes.array.isRequired,
  details: PropTypes.func.isRequired,
  editable: PropTypes.bool.isRequired,
  filtersRequired: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  FormComponent: PropTypes.elementType,
  userAssignedDepartments: PropTypes.array,
};
export default Messages;
